import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";

import { ICON_VARIANTS } from "./constants";
import { ConfirmationModalProps, ConfirmationModalVariant } from "./types";
import { isConfirmationSkipped } from "./utils";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// Base modal component without navigation blocking
const BaseConfirmationModal = ({
  title,
  isNavigationBlocked = false,
  leaveButtonText,
  saveButtonText,
  handleOnConfirm,
  handleLeave,
  handleClose,
  setIsOpen,
  isOpen,
  isLoading,
  description,
  headerVariant = ConfirmationModalVariant.default,
  saveButtonVariant = "default",
  exitButtonVariant = "destructiveOutline",
  MarkdownDescription,
  children,
  requireConfirmation = false,
  confirmationText = "",
  confirmationPlaceholder = "",
  contentClassName,
  overlayClassName,
}: Omit<ConfirmationModalProps, "useBlocker">) => {
  const [confirmationInput, setConfirmationInput] = useState("");
  const isConfirmDisabled = requireConfirmation
    ? confirmationInput !== confirmationText || isLoading
    : isLoading;

  // Handle tab/browser close events
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isNavigationBlocked) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isNavigationBlocked]);

  const onLeave = useCallback(() => {
    handleLeave?.();
    setIsOpen(false);
  }, [handleLeave, setIsOpen]);

  const onConfirm = useCallback(() => {
    if (handleOnConfirm) {
      handleOnConfirm(undefined, undefined);
      return;
    }
    setIsOpen(false);
  }, [handleOnConfirm, setIsOpen]);

  const onClose = useCallback(
    (open?: boolean) => {
      if (open === false || open === undefined) {
        handleClose?.();
        setConfirmationInput("");
        setIsOpen(false);
      }
    },
    [handleClose, setIsOpen],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !isConfirmDisabled) {
        e.preventDefault();
        onConfirm();
      }
    },
    [isConfirmDisabled, onConfirm],
  );

  useEffect(() => {
    if (!isOpen) {
      setConfirmationInput("");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        overlayClassName={overlayClassName}
        className={cn("flex flex-col gap-3", contentClassName)}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {typeof title === "string" && ICON_VARIANTS[headerVariant]}
            {typeof title === "string" ? <span>{title}</span> : title}
          </DialogTitle>
        </DialogHeader>
        <div className="mb-2 flex flex-col gap-y-2">
          {description ? (
            <DialogDescription className="whitespace-pre-line">{description}</DialogDescription>
          ) : null}
          {MarkdownDescription ? (
            <div className="text-sm text-muted-foreground">{MarkdownDescription}</div>
          ) : null}
          {requireConfirmation && (
            <div className="flex flex-col gap-y-2">
              <Input
                className="italic"
                placeholder={confirmationPlaceholder || confirmationText}
                value={confirmationInput}
                onChange={e => setConfirmationInput(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <p className="text-sm text-muted-foreground">
                To confirm, type <b>{confirmationText}</b> in the box above.
              </p>
            </div>
          )}
          {children}
        </div>
        <DialogFooter className="flex w-full">
          <Button variant={exitButtonVariant} onClick={onLeave}>
            {leaveButtonText}
          </Button>
          <Button disabled={isConfirmDisabled} variant={saveButtonVariant} onClick={onConfirm}>
            {isLoading ? <LoadingSpinner size={4} /> : saveButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal with blocker functionality using React Router's proper blocker API
const BlockerConfirmationModal = (props: Omit<ConfirmationModalProps, "useBlocker">) => {
  const {
    isNavigationBlocked = false,
    setIsOpen,
    isOpen,
    handleOnConfirm,
    handleLeave,
    handleClose,
    title,
    description,
    leaveButtonText,
    saveButtonText,
    isLoading,
    headerVariant = ConfirmationModalVariant.default,
    saveButtonVariant = "default",
    exitButtonVariant = "destructiveOutline",
    MarkdownDescription,
    children,
    requireConfirmation = false,
    confirmationText = "",
    confirmationPlaceholder = "",
    contentClassName,
    overlayClassName,
  } = props;

  const navigate = useNavigate();
  const [confirmationInput, setConfirmationInput] = useState("");
  const willProceedRef = useRef(false);
  const isConfirmDisabled = requireConfirmation
    ? confirmationInput !== confirmationText || isLoading
    : isLoading;

  // Use React Router's blocker with proper proceed/reset API
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block same-path navigation
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    // Don't block if navigation blocking is disabled
    if (!isNavigationBlocked) {
      return false;
    }

    // Check if this navigation should skip confirmation (e.g., tab switching)
    const shouldSkip = isConfirmationSkipped({ currentLocation, nextLocation });
    if (shouldSkip) {
      return false;
    }

    // Block the navigation
    return true;
  });

  // Sync modal open state with blocker state
  useEffect(() => {
    if (blocker.state === "blocked") {
      willProceedRef.current = false;
      setIsOpen(true);
    }
  }, [blocker.state, setIsOpen]);

  useEffect(() => {
    if (!isOpen && blocker.state === "blocked" && !willProceedRef.current) {
      const timeoutId = setTimeout(() => {
        if (blocker.state === "blocked" && !willProceedRef.current) {
          blocker.reset();
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, blocker]);

  // Handle tab/browser close events
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isNavigationBlocked) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isNavigationBlocked]);

  // Handle "Leave" button - proceed with navigation
  const onLeave = useCallback(() => {
    handleLeave?.();
    willProceedRef.current = true;
    if (blocker.state === "blocked" && blocker.location) {
      blocker.proceed();
      requestAnimationFrame(() => {
        setIsOpen(false);
      });
    } else {
      setIsOpen(false);
    }
  }, [handleLeave, blocker, setIsOpen]);

  // Handle "Save/Confirm" button
  const onConfirm = useCallback(() => {
    if (handleOnConfirm) {
      const nextPath =
        blocker.state === "blocked" && blocker.location
          ? blocker.location.pathname +
            (blocker.location.search || "") +
            (blocker.location.hash || "")
          : undefined;

      const proceedWithNavigation = () => {
        willProceedRef.current = true;
        if (blocker.state === "blocked") {
          blocker.proceed();
        } else if (nextPath) {
          navigate(nextPath);
        }
      };

      handleOnConfirm(nextPath, proceedWithNavigation);
      setIsOpen(false);
      return;
    }

    willProceedRef.current = true;
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
    setIsOpen(false);
  }, [handleOnConfirm, blocker, setIsOpen, navigate]);

  const onClose = useCallback(
    (open?: boolean) => {
      if (open === false || open === undefined) {
        handleClose?.();
        setConfirmationInput("");
        const shouldProceed = willProceedRef.current;
        if (blocker.state === "blocked" && !shouldProceed) {
          blocker.reset();
          willProceedRef.current = false;
        } else if (!shouldProceed) {
          willProceedRef.current = false;
        }
        setIsOpen(false);
      }
    },
    [handleClose, blocker, setIsOpen],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !isConfirmDisabled) {
        e.preventDefault();
        onConfirm();
      }
    },
    [isConfirmDisabled, onConfirm],
  );

  useEffect(() => {
    if (!isOpen) {
      setConfirmationInput("");
    } else {
      willProceedRef.current = false;
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        overlayClassName={overlayClassName}
        className={cn("flex flex-col gap-3", contentClassName)}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {typeof title === "string" && ICON_VARIANTS[headerVariant]}
            {typeof title === "string" ? <span>{title}</span> : title}
          </DialogTitle>
        </DialogHeader>
        <div className="mb-2 flex flex-col gap-y-2">
          {description ? (
            <DialogDescription className="whitespace-pre-line">{description}</DialogDescription>
          ) : null}
          {MarkdownDescription ? (
            <div className="text-sm text-muted-foreground">{MarkdownDescription}</div>
          ) : null}
          {requireConfirmation && (
            <div className="flex flex-col gap-y-2">
              <Input
                className="italic"
                placeholder={confirmationPlaceholder || confirmationText}
                value={confirmationInput}
                onChange={e => setConfirmationInput(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <p className="text-sm text-muted-foreground">
                To confirm, type <b>{confirmationText}</b> in the box above.
              </p>
            </div>
          )}
          {children}
        </div>
        <DialogFooter className="flex w-full">
          <Button variant={exitButtonVariant} onClick={onLeave}>
            {leaveButtonText}
          </Button>
          <Button disabled={isConfirmDisabled} variant={saveButtonVariant} onClick={onConfirm}>
            {isLoading ? <LoadingSpinner size={4} /> : saveButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main exported component that chooses which implementation to use
export const ConfirmationModal = (props: ConfirmationModalProps) => {
  const { useBlocker: shouldUseBlocker = true } = props;

  if (shouldUseBlocker) {
    return <BlockerConfirmationModal {...props} />;
  }

  return <BaseConfirmationModal {...props} />;
};

export * from "./types";
